/**
 * Le journal du jour — **le premier écran** (web-pwa §5.6).
 *
 * Demandé par Hedi le 2 septembre 2026 : « je pense que si le premier écran est
 * forcément celui où l'élève rentre son journal du jour ». Ce n'est pas un
 * réagencement d'onglets. Le mot du soir existait, il était rangé derrière un
 * lien de la page d'accueil, et il n'a jamais été écrit une seule fois.
 *
 * **Et pas avant la fin des cours** — 16 h, midi le mercredi et le samedi
 * (Hedi, 10 septembre 2026). L'écran s'ouvrait à minuit, donc *pendant* les
 * cours, et un enfant qui ouvrait l'application à 14 h devait raconter des
 * heures qui n'avaient pas encore eu lieu. L'heure est décidée par le serveur
 * (`ouvert`, `ouvre_a`) : celle d'une tablette se règle à la main.
 *
 * **Proposé, plus exigé** (Hedi, 30 septembre 2026). Du 3 au 30 septembre,
 * tant que le journal n'était pas envoyé, les écrans de travail renvoyaient
 * ici ; ce blocage est retiré, parce qu'il retenait les enfants loin des
 * exercices sans obtenir le journal. L'écran reste le premier, et « Rien de
 * spécial aujourd'hui » reste tracé (`nothing_to_say`), donc les parents lisent
 * « rien à dire » plutôt qu'un blanc qu'ils prendraient pour un oubli.
 *
 * **Ce que l'enfant écrit ici, ses parents le lisent** — et l'écran le dit, à
 * l'endroit où l'on écrit, pas dans une page d'accueil qu'on ne relit jamais.
 * C'est l'exact inverse de la règle 4 : la séance ne remonte jamais aux
 * parents, le journal est écrit pour eux.
 *
 * **Il part par la route, pas par la file hors ligne.** La file est indexée par
 * séance (`sendItem` abandonne les articles sans `tutorSessionId`) et c'est
 * précisément là que le mot du soir se perdait avant aujourd'hui : il entrait
 * dans la file, en sortait « envoyé », et n'atteignait aucune table. Hors ligne,
 * l'écran le dit et garde la saisie — il ne prétend pas avoir envoyé.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { call } from "@/api/client";
import type { JourOublie, JournalDuJour, LigneDeJournal } from "@/api/schema";
import { useSession } from "@/app/session";
import { newClientId } from "@/offline/outbox";
import { etiquette } from "@/ui/matieres";
import { Button, Card, ErrorNote, Loading, Screen } from "@/ui/primitives";

/** « vacances de la Toussaint » → « Vacances de la Toussaint ». */
function majuscule(texte: string): string {
  return texte ? texte.charAt(0).toUpperCase() + texte.slice(1) : texte;
}

/**
 * « 16:00 » → « 16 h », « 12:30 » → « 12 h 30 ».
 *
 * L'heure arrive du serveur telle qu'il la décide ; ce qui se dit en français se
 * dit ici. Une chaîne vide rend une chaîne vide — l'écran a alors une phrase
 * sans heure plutôt qu'une phrase avec un trou.
 */
export function heureEnClair(heure: string): string {
  const [h, m] = (heure ?? "").split(":");
  if (!h) return "";
  const minutes = (m ?? "").replace(/^0+$/, "");
  return minutes ? `${Number(h)} h ${minutes}` : `${Number(h)} h`;
}

export function JournalScreen() {
  const { profile } = useSession();
  const navigate = useNavigate();

  const [journal, setJournal] = useState<JournalDuJour | null>(null);
  const [lignes, setLignes] = useState<LigneDeJournal[]>([]);
  const [panne, setPanne] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);
  /** L'enfant a rouvert un journal déjà envoyé pour le corriger. */
  const [correction, setCorrection] = useState(false);

  /**
   * L'identifiant d'idempotence (invariant §14.5), frappé **au moment du tap**.
   *
   * Il survit aux réessais du même envoi — un réseau qui coupe pendant la
   * requête ne doit pas créer deux journaux — et meurt avec lui : une
   * correction faite dix minutes plus tard est un autre envoi, et rejouer le
   * même identifiant la ferait ignorer par le serveur.
   */
  const envoiEnCours = useRef<string | null>(null);

  const charger = useCallback(async () => {
    try {
      const recu = await call("monJournal");
      setJournal(recu);
      setLignes(recu.lignes ?? []);
      setPanne(false);
    } catch {
      setPanne(true);
    }
  }, []);

  useEffect(() => {
    void charger();
  }, [charger]);

  const modifier = (rang: number, champ: "fait" | "coince", valeur: string) =>
    setLignes((etat) =>
      etat.map((ligne, index) => (index === rang ? { ...ligne, [champ]: valeur } : ligne)),
    );

  const remplies = lignes.filter((l) => (l.fait ?? "").trim() || (l.coince ?? "").trim());

  const envoyer = async (rienADire: boolean) => {
    setEnvoi(true);
    setErreur(null);
    envoiEnCours.current ??= newClientId();
    try {
      const rendu = await call("enregistrerMonJournal", {
        body: {
          // « Rien de spécial » efface ce qui traînait dans les champs : le
          // bouton dit ce qu'il envoie, sinon il enverrait autre chose.
          lignes: rienADire ? lignes.map((l) => ({ ...l, fait: "", coince: "" })) : lignes,
          nothing_to_say: rienADire,
          client_id: envoiEnCours.current,
        },
      });
      envoiEnCours.current = null;
      setJournal(rendu);
      setLignes(rendu.lignes ?? []);
      setCorrection(false);
      navigate("/eleve/travail");
    } catch {
      // La saisie reste à l'écran, et l'identifiant reste armé pour le
      // prochain essai : c'est le même envoi qui recommence.
      setErreur(
        navigator.onLine
          ? "Je n'ai pas réussi à envoyer ton journal. Réessaie dans un instant."
          : "Tu es hors ligne. Ton texte reste là ; réessaie quand ça revient.",
      );
    } finally {
      setEnvoi(false);
    }
  };

  if (!journal && !panne) return <Loading label="Je regarde ta journée…" />;

  if (panne || !journal) {
    return (
      <Screen title="Ton journal">
        <ErrorNote>Je n'arrive pas à ouvrir ton journal.</ErrorNote>
        <Button onClick={() => void charger()}>Réessayer</Button>
      </Screen>
    );
  }

  // **Déjà envoyé : on relit, on ne réécrit pas par-dessus sans le vouloir.**
  // L'enfant qui rouvre l'application le soir même retrouve ce qu'il a écrit,
  // et un bouton pour le corriger — pas un formulaire vide qui donnerait
  // l'impression que rien n'a été enregistré.
  if (journal.envoye && !correction) {
    return (
      <Screen title="Ton journal est écrit" lead="Merci. Tu peux passer au travail du jour.">
        {journal.nothing_to_say && remplies.length === 0 ? (
          <Card>
            <p>Tu as répondu « rien de spécial aujourd'hui ». C'est noté tel quel.</p>
          </Card>
        ) : (
          <RelectureDuJournal lignes={lignes} />
        )}
        <div className="journal__suite">
          <Button onClick={() => navigate("/eleve/travail")}>Voir le travail du jour</Button>
          <Button variant="quiet" onClick={() => setCorrection(true)}>
            Corriger mon journal
          </Button>
        </div>
        <RattrapagesEnAttente />
        <p className="aside">
          <Link to="/eleve/journal">Relire mes journaux précédents</Link>
        </p>
      </Screen>
    );
  }

  // **Un jour sans école ne demande rien.** Sans emploi du temps, la journée se
  // repliait sur le profil — la liste de *toutes* les matières de l'enfant — et
  // cet écran lui demandait un dimanche de vacances ce qu'elle avait fait dans
  // huit cours qui n'ont pas eu lieu. Le journal reste ouvert si elle veut y
  // écrire ; il n'est simplement pas proposé.
  if (journal.classe === false) {
    return (
      <Screen
        title="Pas de cours aujourd'hui"
        lead={journal.raison ? majuscule(journal.raison) : "C'est un jour sans école."}
      >
        <Card>
          <p>Rien à raconter, donc. Tu peux réviser si tu veux, ou revenir demain.</p>
        </Card>
        <div className="journal__suite">
          <Button onClick={() => navigate("/eleve/travail")}>Voir ce que je peux réviser</Button>
        </div>
        <RattrapagesEnAttente />
        <p className="aside">
          <Link to="/eleve/journal">Relire mes journaux précédents</Link>
        </p>
      </Screen>
    );
  }

  // **Le journal ne s'ouvre pas pendant les cours** (Hedi, 10 septembre 2026) :
  // 16 h les jours ordinaires, midi le mercredi et le samedi. À 14 h, la question
  // porterait sur des heures qui n'ont pas encore eu lieu.
  //
  // Ce qui reste ici : les journaux oubliés des jours passés — ceux-là s'écrivent
  // à toute heure —, la relecture, et la déclaration d'absence, qui est vraie
  // dès le matin.
  if (journal.ouvert === false) {
    const quand = heureEnClair(journal.ouvre_a ?? "");
    return (
      <Screen
        title="Ta journée n'est pas finie"
        lead={
          quand
            ? `Ton journal s'ouvrira à ${quand}.`
            : "Ton journal s'ouvrira à la fin des cours."
        }
      >
        <Card>
          <p>
            Reviens à ce moment-là pour me raconter comment ça s'est passé. En attendant, tu peux
            travailler.
          </p>
        </Card>
        <div className="journal__suite">
          <Button onClick={() => navigate("/eleve/travail")}>Voir le travail du jour</Button>
        </div>
        <p className="aside">
          Pas cours aujourd'hui ? <Link to="/eleve/absence">Dis-le, et on n'en parle plus.</Link>
        </p>
        <RattrapagesEnAttente />
        <p className="aside">
          <Link to="/eleve/journal">Relire mes journaux précédents</Link>
        </p>
      </Screen>
    );
  }

  return (
    <Screen
      title="Ta journée"
      lead={
        profile.oneInstructionPerScreen
          ? "Deux questions par matière. Laisse vide ce dont tu n'as rien à dire."
          : "Deux questions par matière. Ce qui est vide n'est pas envoyé."
      }
    >
      <ChampsDuJournal journal={journal} lignes={lignes} modifier={modifier} />

      {erreur ? <ErrorNote>{erreur}</ErrorNote> : null}

      <Card>
        <p className="muted small">
          C'est ce que tes parents liront, écrit par toi. Ce que tu dis au tuteur pendant la séance
          ne leur est jamais montré.
        </p>
        <BoutonsDuJournal
          envoyer={(rienADire) => void envoyer(rienADire)}
          envoi={envoi}
          remplies={remplies.length}
          quand="aujourd'hui"
        />
      </Card>

      {/* Le journal n'est plus bloquant (Hedi, 30 septembre 2026) : la barre du
          bas mène déjà partout, mais un formulaire sans autre sortie qu'« Envoyer »
          se lit comme une obligation. */}
      <p className="aside">
        <Link to="/eleve/travail">Plus tard — voir le travail du jour</Link>
      </p>
      <p className="aside">
        Pas cours aujourd'hui ? <Link to="/eleve/absence">Dis-le, et on n'en parle plus.</Link>
      </p>
      <RattrapagesEnAttente />
      <p className="aside">
        <Link to="/eleve/journal">Relire mes journaux précédents</Link>
      </p>
    </Screen>
  );
}


/**
 * « Tu n'as pas écrit ton journal de lundi » — le bouton du rattrapage.
 *
 * Demandé par Hedi le 8 septembre 2026, le lendemain d'un soir où ni Baya ni
 * Mehdi n'avaient écrit le leur : le rituel prévient désormais le parent, et
 * l'enfant n'avait **aucun moyen de combler le trou**. Un manque qu'on ne peut
 * plus combler cesse d'être un manque pour devenir un reproche.
 *
 * **En bas, et silencieux quand il n'y a rien.** Une liste vide n'affiche pas
 * « aucun journal oublié » : ce bandeau transformerait un écran d'écriture en
 * tableau de bord de sa propre assiduité, tous les soirs, y compris ceux où
 * l'enfant n'a rien manqué du tout.
 *
 * **Une panne ne dit rien non plus.** L'appel échoue en silence : c'est un
 * rappel, pas une condition, et une note d'erreur ici ferait croire à
 * l'enfant que son journal du jour n'est pas parti.
 */
export function RattrapagesEnAttente() {
  const [jours, setJours] = useState<JourOublie[]>([]);

  useEffect(() => {
    let vivant = true;
    call("mesJournauxOublies")
      .then((recu) => {
        if (vivant) setJours(recu.jours ?? []);
      })
      .catch(() => undefined);
    return () => {
      vivant = false;
    };
  }, []);

  if (jours.length === 0) return null;

  return (
    <Card title={jours.length === 1 ? "Un journal que tu n'as pas écrit" : "Des journaux que tu n'as pas écrits"}>
      <p className="muted small">
        Tu peux encore les remplir. Ce n'est pas obligatoire, et personne ne t'en veut.
      </p>
      <div className="journal__suite">
        {jours.map((jour) => (
          <Link key={jour.le} className="btn btn--quiet" to={`/eleve/journal/rattrapage/${jour.le}`}>
            {jour.libelle}
          </Link>
        ))}
      </div>
    </Card>
  );
}

/**
 * Les deux sorties du journal — et **« Rien de spécial » demande d'abord une
 * confirmation** (Hedi, 8 septembre 2026 : *« Rien de spécial aujourd'hui doit
 * proposer une confirmation avant de valider »*).
 *
 * Le raccourci reste ce qu'il est depuis le 2 septembre : la moitié sans
 * laquelle un journal réclamé fabrique des journaux inventés. La confirmation ne
 * le décourage donc pas et **ne moralise pas**. « Tu es sûr ? ton journal aide
 * tes parents » rendrait le raccourci coupable, c'est-à-dire referait très
 * exactement le défaut qu'il existe pour empêcher — un enfant pressé écrit
 * alors trois lignes fausses, et le journal se met à mentir. Elle dit ce qui va
 * partir, et rien d'autre.
 *
 * **Dans la page, jamais `window.confirm`.** La coquille Android ne pose qu'un
 * `onShowFileChooser` sur son `WebChromeClient` ; `onJsConfirm` y garde donc
 * son implémentation de base, qui rend `false` — aucune boîte n'est affichée et
 * `confirm()` répond « non ». Sur les deux seuls appareils où cette application
 * tourne, le bouton serait mort, en silence, et la soirée resterait fermée.
 *
 * **L'affirmative n'est jamais sous le doigt qui vient d'appuyer.** La question
 * pousse la rangée vers le bas, et la sortie occupe la place que « Rien de
 * spécial » occupait : un double tap — ce que fait un enfant quand rien ne
 * semble se passer — annule au lieu d'envoyer.
 *
 * **La question reste posée si l'envoi échoue.** La rabattre sur un réseau qui
 * coupe redemanderait deux gestes pour le même envoi, alors que la décision,
 * elle, a bien été prise.
 */
export function BoutonsDuJournal({
  envoyer,
  envoi,
  remplies,
  quand,
}: {
  envoyer: (rienADire: boolean) => void;
  envoi: boolean;
  remplies: number;
  quand: string;
}) {
  const [confirmation, setConfirmation] = useState(false);

  if (confirmation) {
    return (
      <div className="journal__confirmation">
        <p>Tu envoies ton journal sans rien raconter. Tes parents liront « rien de spécial ».</p>
        {/* Le raccourci **efface** les champs — le bouton dit ce qu'il envoie.
            C'est la seule perte possible ici, donc la seule qui s'annonce. */}
        {remplies > 0 ? (
          <p className="muted small">Ce que tu as commencé à écrire ne partira pas.</p>
        ) : null}
        <div className="journal__suite">
          <Button onClick={() => envoyer(true)} disabled={envoi}>
            Oui, rien de spécial
          </Button>
          <Button variant="quiet" onClick={() => setConfirmation(false)} disabled={envoi}>
            Non, je vais écrire
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="journal__suite">
      <Button onClick={() => envoyer(false)} disabled={envoi || remplies === 0}>
        Envoyer
      </Button>
      {/* Le raccourci a le même poids visuel qu'une sortie, pas qu'un refus :
          une journée sans rien à signaler est une journée normale. */}
      <Button variant="quiet" onClick={() => setConfirmation(true)} disabled={envoi}>
        Rien de spécial {quand}
      </Button>
    </div>
  );
}

/**
 * Les champs d'un journal : les mêmes pour aujourd'hui et pour un jour rattrapé.
 *
 * **Extrait plutôt que recopié.** Le rattrapage d'un jour oublié (8 septembre
 * 2026) pose exactement les deux mêmes questions ; deux copies auraient divergé
 * sur le cours annulé, sur l'annonce du cahier de textes ou sur la mention du
 * repli, et l'enfant aurait vu un formulaire différent selon le jour qu'il
 * raconte, sans que rien ne dise lequel est juste.
 */
export function ChampsDuJournal({
  journal,
  lignes,
  modifier,
}: {
  journal: JournalDuJour;
  lignes: LigneDeJournal[];
  modifier: (rang: number, champ: "fait" | "coince", valeur: string) => void;
}) {
  return (
    <>
    {journal.source === "profil" && lignes.length > 0 ? (
      <p className="muted small">
        Voici tes matières de l'année — je n'ai pas ton emploi du temps d'aujourd'hui. Remplis
        seulement celles que tu as eues.
      </p>
    ) : null}

    {journal.perimee ? (
      <p className="muted small">
        Mon emploi du temps date un peu. S'il manque un cours, ajoute-le dans la matière la plus
        proche.
      </p>
    ) : null}

    {lignes.length === 0 ? (
      <Card>
        <p>Je n'ai pas la liste de tes matières aujourd'hui. Dis-moi juste comment ça s'est passé.</p>
      </Card>
    ) : null}

    {lignes.map((ligne, rang) =>
      ligne.annule ? (
        // **Un cours annulé reste affiché, barré.** L'omettre ferait croire à
        // l'enfant qu'elle a oublié une matière, et elle chercherait quoi écrire.
        <Card key={`${ligne.creneau}-${ligne.matiere}-${rang}`}>
          <p className="muted">
            {ligne.creneau ? `${ligne.creneau} · ` : ""}
            <s>{etiquette(ligne.matiere)}</s> — pas eu lieu.
          </p>
        </Card>
      ) : (
        <Card key={`${ligne.creneau}-${ligne.matiere}-${rang}`} title={etiquette(ligne.matiere)}>
          {ligne.creneau ? <p className="muted small">{ligne.creneau}</p> : null}
          {/* Le cahier de textes **pré-remplit, il ne valide pas** : ce qui
              était annoncé est rappelé à côté du champ, jamais dedans. Écrit
              dedans, il repartirait tel quel comme si l'enfant l'avait dit. */}
          {ligne.propose ? <p className="muted small">Annoncé : {ligne.propose}</p> : null}

          <label className="field">
            <span className="field__label">Ce que tu as fait</span>
            <textarea
              className="textarea"
              rows={2}
              value={ligne.fait ?? ""}
              onChange={(e) => modifier(rang, "fait", e.target.value)}
            />
          </label>

          <label className="field">
            <span className="field__label">Ce qui a coincé</span>
            <textarea
              className="textarea"
              rows={2}
              placeholder="Rien, si tout allait bien."
              value={ligne.coince ?? ""}
              onChange={(e) => modifier(rang, "coince", e.target.value)}
            />
          </label>
        </Card>
      ),
    )}
    </>
  );
}

/** Ce qui a été écrit, relu tel quel — aucun champ, aucune reformulation. */
export function RelectureDuJournal({ lignes }: { lignes: LigneDeJournal[] }) {
  const dites = lignes.filter((l) => (l.fait ?? "").trim() || (l.coince ?? "").trim());
  if (dites.length === 0) {
    return (
      <Card>
        <p className="muted">Rien d'écrit ce jour-là.</p>
      </Card>
    );
  }
  return (
    <>
      {dites.map((ligne, rang) => (
        <Card key={`${ligne.creneau}-${ligne.matiere}-${rang}`} title={etiquette(ligne.matiere)}>
          {ligne.fait?.trim() ? (
            <p>
              <span className="field__label">Fait : </span>
              {ligne.fait}
            </p>
          ) : null}
          {ligne.coince?.trim() ? (
            <p>
              <span className="field__label">A coincé : </span>
              {ligne.coince}
            </p>
          ) : null}
        </Card>
      ))}
    </>
  );
}
